<template>
  <div class="flex flex-col gap-4">
    <div class="flex gap-4 items-center">
      <h1 class="text-text-primary text-2xl font-semibold flex flex-1">{{ $t('coach.shared.all_statistics') }}</h1>

      <Menu as="div" class="relative inline-block text-left w-full lg:w-auto">
        <MenuButton class="relative w-full lg:w-auto disabled:bg-gray-400 inline-flex h-8 items-center px-2 py-2 border border-gray-700 shadow-sm text-sm font-medium rounded-md text-text-primary bg-gray-800 hover:bg-gray-700">
          <div class="flex-1 text-left font-light flex gap-1">
            <span class="text-text-secondary">{{ $t('coach.statistics.weapon_type') }}</span>
            <span v-if="selectedWeaponType">{{ selectedWeaponType }}</span>
            <span v-else>{{ $t('common.all') }}</span>
          </div>
          <ChevronDownIcon class="-mr-1 ml-4 h-5 w-5" aria-hidden="true" />
        </MenuButton>
        <transition enter-active-class="transition ease-out duration-100" enter-from-class="transform opacity-0 scale-95" enter-to-class="transform opacity-100 scale-100" leave-active-class="transition ease-in duration-75" leave-from-class="transform opacity-100 scale-100" leave-to-class="transform opacity-0 scale-95">
          <MenuItems class="absolute right-0 z-50 mt-2 w-full lg:w-[192px] origin-top-right rounded-md bg-gray-800 border border-gray-700 shadow-lg focus:outline-none">
            <div class="py-1 flex flex-col px-4">
              <MenuItem @click.prevent="$emit('update:weaponType', '')">
                <a href="#" :class="[!selectedWeaponType ? 'text-text-primary' : 'text-text-secondary', 'group flex items-center py-2 text-sm w-full justify-start font-light']">
                  {{ $t('common.all') }}
                  <CheckIcon v-if="!selectedWeaponType" class="h-5 w-5 ml-auto text-primary-400" />
                </a>
              </MenuItem>
              <MenuItem v-for="wt in Object.keys(availableWeaponTypes)" :key="wt" @click.prevent="$emit('update:weaponType', wt)">
                <a href="#" :class="[wt === selectedWeaponType ? 'text-text-primary' : 'text-text-secondary', 'group flex items-center py-2 text-sm w-full justify-start']">
                  {{ wt }}
                  <CheckIcon v-if="wt === selectedWeaponType" class="h-5 w-5 ml-auto text-primary-400" />
                </a>
              </MenuItem>
            </div>
          </MenuItems>
        </transition>
      </Menu>

      <Menu as="div" class="relative inline-block text-left w-full lg:w-auto">
        <MenuButton class="relative w-full lg:w-auto disabled:bg-gray-400 inline-flex h-8 items-center px-2 py-2 border border-gray-700 shadow-sm text-sm font-medium rounded-md text-text-primary bg-gray-800 hover:bg-gray-700">
          <div class="flex-1 text-left font-light flex gap-1">
            <span class="text-text-secondary">{{ $t('coach.statistics.weapon') }}</span>
            <span v-if="selectedWeapon">{{ selectedWeapon }}</span>
            <span v-else>{{ $t('common.all') }}</span>
          </div>
          <ChevronDownIcon class="-mr-1 ml-4 h-5 w-5" aria-hidden="true" />
        </MenuButton>
        <transition enter-active-class="transition ease-out duration-100" enter-from-class="transform opacity-0 scale-95" enter-to-class="transform opacity-100 scale-100" leave-active-class="transition ease-in duration-75" leave-from-class="transform opacity-100 scale-100" leave-to-class="transform opacity-0 scale-95">
          <MenuItems class="absolute right-0 z-50 mt-2 w-full lg:w-[192px] origin-top-right rounded-md bg-gray-800 border border-gray-700 shadow-lg focus:outline-none">
            <div class="py-1 flex flex-col px-4">
              <MenuItem @click.prevent="$emit('update:weapon', '')">
                <a href="#" :class="[!selectedWeapon ? 'text-text-primary' : 'text-text-secondary', 'group flex items-center py-2 text-sm w-full justify-start font-light']">
                  {{ $t('common.all') }}
                  <CheckIcon v-if="!selectedWeapon" class="h-5 w-5 ml-auto text-primary-400" />
                </a>
              </MenuItem>
              <MenuItem v-for="w in availableWeapons" :key="w.name" :disabled="!w.has_data" @click.prevent="w.has_data && $emit('update:weapon', w.name)">
                <a
                  href="#"
                  :class="[
                    !w.has_data ? 'text-gray-600 cursor-not-allowed' : w.name === selectedWeapon ? 'text-text-primary' : 'text-text-secondary',
                    'group flex items-center py-2 text-sm w-full justify-start',
                  ]"
                >
                  {{ w.name }}
                  <span v-if="!w.has_data" class="ml-auto text-gray-600 text-xs">{{ $t('coach.statistics.no_data') }}</span>
                  <CheckIcon v-else-if="w.name === selectedWeapon" class="h-5 w-5 ml-auto text-primary-400" />
                </a>
              </MenuItem>
            </div>
          </MenuItems>
        </transition>
      </Menu>
    </div>

    <div class="grid lg:grid-cols-2 w-full gap-6">
      <StatComparisonBlock
        :title="$t('coach.stat_groups.general')"
        :hasWeaponFilter="hasWeaponFilter"
        :statsMap="stats"
        :source="breakdownSource"
        :stats="rows(GENERAL)"
      />
      <StatComparisonBlock
        :title="$t('coach.stat_groups.aim')"
        :hasWeaponFilter="hasWeaponFilter"
        :statsMap="stats"
        :source="breakdownSource"
        :stats="rows(AIM)"
      />
      <div v-if="hasWeaponFilter" class="bg-gray-900 rounded-lg p-6 flex flex-col items-center justify-center gap-2 text-center">
        <h2 class="text-text-primary font-medium">{{ $t('coach.stat_groups.utility') }}</h2>
        <p class="text-gray-600 text-sm">{{ $t('coach.statistics.utility_not_per_weapon') }}</p>
      </div>
      <StatComparisonBlock
        v-else
        :title="$t('coach.stat_groups.utility')"
        :statsMap="stats"
        :source="breakdownSource"
        :stats="rows(UTILITY)"
      />
      <StatComparisonBlock
        :title="$t('coach.stat_groups.entry_trades')"
        :hasWeaponFilter="hasWeaponFilter"
        :statsMap="stats"
        :source="breakdownSource"
        :stats="rows(ENTRY_TRADES)"
      />
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  Menu, MenuButton, MenuItems, MenuItem,
} from '@headlessui/vue';
import { ChevronDownIcon, CheckIcon } from '@heroicons/vue/24/outline';
import StatComparisonBlock from '../StatComparisonBlock.vue';

const { t } = useI18n();

// [stat key, perWeapon]; label and description come from coach.stat_labels / coach.stat_descriptions.
const GENERAL = [
  ['refrag_rating', false], ['kills', true], ['assists', true], ['deaths', false],
  ['kpr', true], ['headshot_kill_percentage', true], ['adr', true], ['hltv_rating', false],
];
const AIM = [
  ['time_to_kill_average', true], ['time_to_damage_average', true], ['spotted_hit_rate', true],
  ['headshot_percentage', true], ['accurate_shots_while_counterstrafing_rate', true],
  ['shots_at_low_velocity_rate', false], ['velocity_control_rate', false],
  ['crosshair_placement_average', true], ['spray_shot_accuracy', true],
];
const UTILITY = [
  ['util_flash_assists'], ['util_flash_enemies_flashed'], ['util_flash_friends_flashed'],
  ['average_enemy_flash_time'], ['util_enemy_damage'], ['util_lost_cost'],
];
const ENTRY_TRADES = [
  ['opening_successes', true], ['opening_fails', true], ['trade_kill_successes', true],
  ['trade_kill_fails', true], ['traded_death_successes', false], ['traded_death_fails', false],
];

const props = defineProps({
  stats: { type: Object, required: true },
  selectedWeaponType: String,
  selectedWeapon: String,
  availableWeaponTypes: Object,
  availableWeapons: Array,
});

defineEmits(['update:weaponType', 'update:weapon']);

const hasWeaponFilter = computed(() => !!props.selectedWeaponType || !!props.selectedWeapon);

// With a weapon filter the stats prop already carries weapon-scoped values
const breakdownSource = computed(() => (hasWeaponFilter.value ? 'weapon' : 'profile'));

// ADR is an abbreviation and stays untranslated.
function rows(group) {
  return group.map(([key, perWeapon]) => ({
    key,
    label: key === 'adr' ? 'ADR' : t(`coach.stat_labels.${key}`),
    stat: props.stats[key],
    description: t(`coach.stat_descriptions.${key}`),
    ...(perWeapon === undefined ? {} : { perWeapon }),
  }));
}
</script>
